import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getFetchConfig } from '../config/api';
import './QuoteDetails.css';

const QuoteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadQuoteDetails();
  }, [id]);

  const loadQuoteDetails = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.QUOTES, getFetchConfig('GET', null, true));
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'ok') {
        const foundQuote = data.quotes.find(q => q.id.toString() === id);
        if (foundQuote) {
          setQuote(foundQuote);
        } else {
          setMessage('Orçamento não encontrado');
        }
      }
    } catch (error) {
      setMessage('Erro ao carregar orçamento: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9500';
      case 'analisando': return '#007bff';
      case 'aprovado': return '#28a745';
      case 'recusado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="quote-details-container">
        <div className="loading">Carregando detalhes do orçamento...</div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="quote-details-container">
        <div className="error-message">{message}</div>
        <button 
          onClick={() => navigate('/orders')} 
          className="btn btn-secondary"
        >
          Voltar aos Meus Pedidos
        </button>
      </div>
    );
  }

  const customerInfo = quote.customerInfo || {};
  const isAdvanced = quote.type === 'advanced';

  return (
    <div className="quote-details-container">
      <div className="quote-details-header">
        <button 
          onClick={() => navigate('/orders')} 
          className="btn btn-secondary"
        >
          ← Voltar aos Meus Pedidos
        </button>
        <h1>Detalhes do Orçamento #{quote.id}</h1>
      </div>

      <div className="quote-details-content">
        {/* Informações do Cliente */}
        <div className="info-section">
          <h2>Suas Informações</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nome:</label>
              <span>{customerInfo.name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{customerInfo.email || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Telefone:</label>
              <span>{customerInfo.phone !== 'undefined' ? customerInfo.phone : 'N/A'}</span>
            </div>
            {isAdvanced && (
              <>
                <div className="info-item">
                  <label>Empresa:</label>
                  <span>{customerInfo.company || 'N/A'}</span>
                </div>
                <div className="info-item full-width">
                  <label>Endereço:</label>
                  <span>{customerInfo.address || 'N/A'}</span>
                </div>
              </>
            )}
            {!isAdvanced && (
              <>
                <div className="info-item">
                  <label>Tipo de Propriedade:</label>
                  <span>{customerInfo.propertyType || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Preferência de Contacto:</label>
                  <span>{customerInfo.contactPreference || 'N/A'}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Produtos Selecionados (apenas para orçamentos avançados) */}
        {isAdvanced && quote.products && quote.products.length > 0 && (
          <div className="info-section">
            <h2>Itens do Seu Pedido</h2>
            <div className="products-list">
              {quote.products.map((product) => (
                <div key={product.id || product.product_id} className="product-item">
                  <div className="product-header">
                    <h3 className="product-name">{product.name}</h3>
                  </div>
                  <div className="product-details">
                    <div className="detail-row">
                      <span className="label">Quantidade:</span>
                      <span className="value">{product.quantity}</span>
                    </div>
                    {product.price && (
                      <>
                        <div className="detail-row">
                          <span className="label">Preço unitário:</span>
                          <span className="value">€{parseFloat(product.price).toFixed(2)}</span>
                        </div>
                        <div className="detail-row subtotal">
                          <span className="label">Subtotal:</span>
                          <span className="value">€{(parseFloat(product.price) * product.quantity).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div className="total-section">
                <div className="total-row">
                  <span className="total-label">Total do Pedido:</span>
                  <span className="total-value">€{quote.products.reduce((total, product) => 
                    total + (parseFloat(product.price || 0) * product.quantity), 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mensagem/Observações */}
        <div className="info-section">
          <h2>{isAdvanced ? 'Suas Observações' : 'Sua Mensagem'}</h2>
          <div className="message-content">
            {isAdvanced 
              ? (quote.notes || customerInfo.notes || 'Nenhuma observação')
              : (quote.message || 'Nenhuma mensagem')
            }
          </div>
        </div>

        {/* Status e Datas */}
        <div className="info-section">
          <h2>Status do Orçamento</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Status Atual:</label>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(quote.status) }}
              >
                {quote.status.toUpperCase()}
              </span>
            </div>
            <div className="info-item">
              <label>Data de Solicitação:</label>
              <span>
                {new Date(quote.createdAt).toLocaleString('pt-PT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            <div className="info-item">
              <label>Última Atualização:</label>
              <span>
                {new Date(quote.updatedAt).toLocaleString('pt-PT', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Informação sobre o processo */}
        <div className="info-section">
          <h2>Sobre o Seu Orçamento</h2>
          <div className="info-message">
            <p>O seu orçamento está sendo analisado pela nossa equipa. Entraremos em contacto consigo em breve com mais informações.</p>
            <p>Se tiver alguma dúvida, pode contactar-nos através do email ou telefone fornecidos.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetails;
